/**
 * StealthVeil — a stealthed player visibly fades (plan 29 F9, lane BY
 * 2026-10-11).
 *
 * Vanish's stealth was real on the server (stealthGuard: mobs, bots and PvP
 * skip the player) but nothing on any screen showed it: no fade in any frame.
 * Now the stealth window of the shared table (`STEALTH_ABILITIES`) fades the
 * caster's sprite — to `selfAlpha` on the rogue's own screen (they still need
 * to see themselves) and to `observerAlpha` on everyone else's — and fades it
 * back when the window ends. The smoke burst is the ability's own cast effect.
 *
 * Presentation only (no gameplay state). Pixi 8 rule: alpha only, no redraw.
 * Reduced motion keeps the read: the alpha changes at once, without the fade.
 */
import { stealthDurationForAbility } from '../../../shared/combat/stealthAbilities';

export const STEALTH_VEIL_CONFIG = {
  /** The stealthed player's alpha on their own screen. */
  selfAlpha: 0.35,
  /** …and on everyone else's. */
  observerAlpha: 0.12,
  /** Fade into and out of the veil (ms). */
  fadeInMs: 180,
  fadeOutMs: 260,
} as const;

export interface VeilSprite { setAlpha(alpha: number): unknown }

export interface StealthVeilDeps {
  /** The sprite drawing player `id` (the local player's own, or a remote's), or null. */
  spriteFor(id: string, local: boolean): VeilSprite | null;
  reducedMotion(): boolean;
}

export class StealthVeil {
  private veils = new Map<string, { timer: ReturnType<typeof setTimeout>; raf: number; local: boolean }>();

  constructor(private readonly deps: StealthVeilDeps) {}

  /** A cast by `casterId` (`local` = this screen's player): veil them for the ability's stealth window. */
  onCast(casterId: string | undefined, abilityId: string | undefined, local: boolean): void {
    const windowMs = abilityId ? stealthDurationForAbility(abilityId) : 0;
    if (!casterId || windowMs <= 0) return;
    const sprite = this.deps.spriteFor(casterId, local);
    if (!sprite) return;
    this.clear(casterId);
    const target = local ? STEALTH_VEIL_CONFIG.selfAlpha : STEALTH_VEIL_CONFIG.observerAlpha;
    const entry = { timer: 0 as unknown as ReturnType<typeof setTimeout>, raf: 0, local };
    this.veils.set(casterId, entry);
    this.fade(entry, sprite, 1, target, STEALTH_VEIL_CONFIG.fadeInMs);
    entry.timer = setTimeout(() => {
      // Re-read: the sprite may have been rebuilt during the window.
      const now = this.deps.spriteFor(casterId, local) ?? sprite;
      this.fade(entry, now, target, 1, STEALTH_VEIL_CONFIG.fadeOutMs, () => this.veils.delete(casterId));
    }, windowMs);
  }

  /** Drop every veil and restore full alpha (teardown). */
  teardown(): void {
    for (const [id, v] of [...this.veils]) {
      this.clear(id);
      this.deps.spriteFor(id, v.local)?.setAlpha(1);
    }
  }

  private clear(id: string): void {
    const v = this.veils.get(id);
    if (!v) return;
    clearTimeout(v.timer);
    if (v.raf && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(v.raf);
    this.veils.delete(id);
  }

  private fade(entry: { raf: number }, sprite: VeilSprite, from: number, to: number, ms: number, done?: () => void): void {
    if (this.deps.reducedMotion() || typeof requestAnimationFrame !== 'function' || ms <= 0) {
      sprite.setAlpha(to);
      done?.();
      return;
    }
    const start = performance.now();
    const tick = () => {
      const t = Math.min(1, (performance.now() - start) / ms);
      sprite.setAlpha(from + (to - from) * t);
      if (t < 1) entry.raf = requestAnimationFrame(tick);
      else { entry.raf = 0; done?.(); }
    };
    tick();
  }
}
